import { Inject, Injectable, Signal } from '@angular/core'
import { CanMatch, Route, Router, UrlSegment, UrlTree } from '@angular/router'
import { AppMode } from '@ftr/contracts/api/user'
import { APP_MODE } from '@ftr/routing-paths'
import { LaunchDarklyService } from '@ftr/ui-feature-flags'
import { AuthenticationService } from '../../services'

const flagName = 'public-navigation'

/**
 * On the site portal, when the public-navigation feature flag is enabled, redirect any
 * non-home-page URL to the homepage. Implemented as a CanMatch guard so the redirect
 * short-circuits route matching before AuthenticationGuard can imperatively redirect to
 * /login. Will be replaced with a Cloudfront Function in CO-5019.
 */
@Injectable({
  providedIn: 'root',
})
export class RedirectSitePortalToHomeIfPublicNavigationGuard implements CanMatch {
  constructor(
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router,
    @Inject(APP_MODE) private readonly appMode: Signal<AppMode>,
  ) {}

  async canMatch(_route: Route, segments: UrlSegment[]): Promise<boolean | UrlTree> {
    if (this.appMode() !== 'portal') {
      return true
    }

    // No segments means we're already at the home URL ('/'); allow so we don't loop.
    if (segments.length === 0) {
      return true
    }

    const featureFlagEnabled = this.authenticationService.currentUser
      ? await this.launchDarklyService.getFlagVariationForUser(this.authenticationService.currentUser, flagName)
      : await this.launchDarklyService.getFlagVariationForAnonymous(flagName)

    if (!featureFlagEnabled) {
      return true
    }

    return this.router.createUrlTree(['/'])
  }
}
